import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, ChevronRight, Check, Eye } from "react-feather";
import {
  selectRecentlyViewed,
  selectInProgressCookingProgresses,
  formatDuration,
  timeAgo,
  useT,
  DIFFICULTY_LABEL_KEYS,
  selectLocale,
  useAppSelector,
  type RootState,
} from "@reciperoll/core";
import { SectionHeader } from "./SectionHeader";
import { RecipeImage } from "../RecipeImage";

const RECENTLY_VIEWED_LIMIT = 3;

// The last few recipes you opened, as full-width rows with a "time ago" stamp.
export default function RecentlyViewed() {
  const t = useT();
  const locale = useAppSelector(selectLocale);
  const recipes = useAppSelector((s: RootState) =>
    selectRecentlyViewed(s, RECENTLY_VIEWED_LIMIT),
  );
  const cookingIds = new Set(
    useAppSelector(selectInProgressCookingProgresses).map((cp) => cp.recipeId),
  );

  // Keep the "time ago" labels fresh without a navigation.
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(interval);
  }, []);

  if (!recipes.length) return null;

  return (
    <div className="mb-1">
      <SectionHeader
        title={t("home.recentlyViewed")}
        icon={<Eye className="w-5 h-5" />}
        eyebrow={t("home.recentlyViewedEyebrow")}
        link="/recently-viewed"
      />
      <div className="rounded-2xl border border-line bg-surface overflow-hidden divide-y divide-line">
        {recipes.map((recipe) => (
          <Link
            key={recipe.id}
            to={`/recipe/${recipe.id}`}
            className="relative flex items-center gap-3.5 h-[84px] px-4 group hover:bg-surface-2 transition-colors"
          >
            {cookingIds.has(recipe.id) && (
              <span className="absolute left-0 top-3 bottom-3 w-1 rounded-full bg-terracotta" />
            )}
            <div className="relative shrink-0">
              <div className="w-16 h-16 rounded-xl overflow-hidden bg-terracotta-wash">
                <RecipeImage recipe={recipe} />
              </div>
              {recipe.cooked && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center border-2 border-surface">
                  <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                </span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-semibold text-ink mb-0.5 truncate group-hover:text-terracotta-ink transition-colors">
                {recipe.title}
              </p>
              <p className="text-sm text-ink-soft flex items-center gap-1 truncate">
                {!!recipe.cookingDuration && (
                  <>
                    <Clock className="w-3 h-3 text-terracotta shrink-0" />
                    {formatDuration(recipe.cookingDuration, t)} ·{" "}
                  </>
                )}
                {recipe.difficulty ? `${t(DIFFICULTY_LABEL_KEYS[recipe.difficulty])} · ` : ""}
                {timeAgo(recipe.viewedAt, locale)}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-ink-faint shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  );
}
